// GS Mandal — Trust Admin Tender Management module.
// Route: /admin/tenders

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, Plus, Save, Send, X, Trash2, Upload, Download, RefreshCw, Archive, XCircle, CheckCircle2, Loader2, ArrowLeft, Eye, CalendarClock, ListChecks, ExternalLink, User, Mail, Phone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const STATUSES = ["Draft", "Published", "Closed", "Cancelled", "Archived"];
const FILENAME_RE = /^[A-Za-z0-9]+\.pdf$/i;

const emptyForm = () => ({
  tender_type: "", category: "", subcategory: "", subcategories: [],
  entity_scope: "Trust", institute_id: "", institute_name: "",
  title: "", reference_number: "", short_description: "", long_description: "",
  publication_date: "", bid_opening_date: "", last_submission_datetime: "",
  pre_bid_meeting_date: "", clarification_deadline: "",
  technical_bid_opening_date: "", financial_bid_opening_date: "", validity_date: "",
  eligibility_criteria: "", submission_details: "",
  has_tender_fee: false, tender_fee: 0,
  submission_mode: "Online",
});

const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

const inputCls = "w-full border border-[#e5e0d8] rounded-sm px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#234333]";

export default function TenderManagement() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "editor", "infrastructure"].includes(user?.role);
  const [view, setView] = useState("list"); // list | form | detail
  const [tab, setTab] = useState("Draft");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masters, setMasters] = useState({ types: [], categories: [], subcategories_by_category: {}, institutes: [] });
  const [current, setCurrent] = useState(null); // Tender being viewed/edited
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showExtend, setShowExtend] = useState(false);
  const [extendDt, setExtendDt] = useState("");
  const [corr, setCorr] = useState({ title: "", note: "", corr_date: "", file: null });
  const [preview, setPreview] = useState(false);
  const [submissionsOpen, setSubmissionsOpen] = useState(false);

  const loadMasters = async () => {
    try {
      const { data } = await api.get("/public/tenders/masters");
      setMasters({
        types: data?.types || [], categories: data?.categories || [],
        subcategories_by_category: data?.subcategories_by_category || {},
        institutes: data?.institutes || [],
      });
    } catch { /* silent */ }
  };
  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tenders", { params: { status: tab } });
      setList(data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadMasters(); }, []);
  useEffect(() => { if (view === "list") loadList(); }, [tab, view]);

  const openCreate = () => { setCurrent(null); setForm(emptyForm()); setView("form"); };
  const openEdit = (t) => {
    setCurrent(t);
    const subs = (t.subcategories && t.subcategories.length) ? t.subcategories : (t.subcategory ? [t.subcategory] : []);
    setForm({ ...emptyForm(), ...t, subcategories: subs, submission_mode: t.submission_mode === "Offline" ? "Offline" : "Online" });
    setView("form");
  };
  const openDetail = async (t) => {
    try {
      const { data } = await api.get(`/admin/tenders/${t.id}`);
      setCurrent(data); setView("detail");
    } catch { toast.error("Failed to open tender"); }
  };

  const saveForm = async ({ publish = false } = {}) => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.tender_type.trim()) return toast.error("Tender Type is required");
    if (form.has_tender_fee && (!form.tender_fee || parseFloat(form.tender_fee) <= 0)) {
      return toast.error("Please enter a Tender Fee amount greater than zero, or uncheck 'Tender fee applicable'");
    }
    setSaving(true);
    try {
      let saved;
      const payload = {
        ...form,
        subcategories: form.subcategories || [],
        subcategory: (form.subcategories && form.subcategories[0]) || "",
        has_tender_fee: !!form.has_tender_fee,
        tender_fee: form.has_tender_fee ? parseFloat(form.tender_fee || 0) : 0,
        submission_mode: form.submission_mode === "Offline" ? "Offline" : "Online",
      };
      if (current?.id) {
        const { data } = await api.put(`/admin/tenders/${current.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/admin/tenders", payload);
        saved = data;
      }
      if (publish && saved?.status !== "Published") {
        const { data } = await api.patch(`/admin/tenders/${saved.id}/status`, { status: "Published" });
        saved = data;
      }
      toast.success(publish ? "Tender published" : "Saved");
      setCurrent(saved); setView("detail");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const changeStatus = async (t, status) => {
    try {
      const { data } = await api.patch(`/admin/tenders/${t.id}/status`, { status });
      toast.success(`Marked ${status}`);
      setCurrent(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/tenders/${confirmDelete.id}`);
      toast.success("Tender deleted");
      setConfirmDelete(null); setView("list"); setCurrent(null); loadList();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const uploadAttachment = async (file, kind, label) => {
    if (!current?.id) return toast.error("Save the tender first");
    if (!FILENAME_RE.test(file.name)) return toast.error("Filename must be alphanumeric only, ending in .pdf");
    if (file.type !== "application/pdf") return toast.error("Only PDF files allowed");
    if (file.size > 10 * 1024 * 1024) return toast.error("File must be 10 MB or smaller");
    const fd = new FormData();
    fd.append("kind", kind); fd.append("label", label || file.name); fd.append("file", file, file.name);
    try {
      const { data } = await api.post(`/admin/tenders/${current.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCurrent(data);
      toast.success("Uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };

  const deleteAttachment = async (a) => {
    if (!a?.public_id) return;
    const b64 = btoa(a.public_id).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    try {
      await api.delete(`/admin/tenders/${current.id}/attachments/${b64}`);
      const fresh = await api.get(`/admin/tenders/${current.id}`);
      setCurrent(fresh.data);
      toast.success("Removed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const extendDeadline = async () => {
    if (!extendDt) return toast.error("Choose the new date & time");
    try {
      const { data } = await api.patch(`/admin/tenders/${current.id}/extend`, { last_submission_datetime: extendDt });
      setCurrent(data); setShowExtend(false); setExtendDt("");
      toast.success("Deadline extended and corrigendum recorded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const addCorrigendum = async () => {
    if (!corr.title.trim()) return toast.error("Title required");
    if (corr.file && (!FILENAME_RE.test(corr.file.name) || corr.file.type !== "application/pdf")) return toast.error("Attachment must be PDF with alphanumeric filename");
    const fd = new FormData();
    fd.append("title", corr.title.trim()); fd.append("note", corr.note.trim()); fd.append("corr_date", corr.corr_date);
    if (corr.file) fd.append("file", corr.file, corr.file.name);
    try {
      const { data } = await api.post(`/admin/tenders/${current.id}/corrigenda`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCurrent(data); setCorr({ title: "", note: "", corr_date: "", file: null });
      toast.success("Corrigendum added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const subs = useMemo(() => masters.subcategories_by_category[form.category] || [], [form.category, masters]);

  // ---------------------------------------------------------------------------
  // Renders
  // ---------------------------------------------------------------------------
  if (view === "form") return (
    <div className="space-y-5" data-testid="adm-tender-form">
      <div className="bg-white rounded-sm border border-[#e5e0d8] p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <button type="button" onClick={() => setView("list")} className="text-[10px] uppercase tracking-wider text-[#234333] hover:text-[#c78e42] inline-flex items-center gap-1"><ArrowLeft size={11} /> Tenders</button>
            <h1 className="font-serif text-2xl text-[#1c1a17] mt-1">{current ? "Edit Tender" : "Create New Tender"}</h1>
          </div>
          {current && <span className="text-[10px] uppercase tracking-wider text-[#c78e42] font-bold px-2 py-1 border border-[#c78e42] rounded-sm">{current.status}</span>}
        </div>

        <div className="mt-5 grid md:grid-cols-2 gap-3">
          <Fld label="Tender Type *"><select className={inputCls} value={form.tender_type} onChange={(e) => setForm({ ...form, tender_type: e.target.value })} data-testid="adm-tender-type"><option value="">Select…</option>{masters.types.map((t) => <option key={t}>{t}</option>)}</select></Fld>
          <Fld label="Category"><select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, subcategories: [] })} data-testid="adm-tender-category"><option value="">Select…</option>{masters.categories.map((c) => <option key={c}>{c}</option>)}</select></Fld>
          <Fld label="Subcategory" full>
            {!form.category ? (
              <div className="bg-[#fbf8f1] border border-dashed border-[#e5e0d8] rounded-sm px-3 py-2 text-[11px] text-[#8a8478]" data-testid="adm-tender-subcategory-empty">Select a Category first to see available subcategories.</div>
            ) : subs.length === 0 ? (
              <div className="bg-[#fbf8f1] border border-dashed border-[#e5e0d8] rounded-sm px-3 py-2 text-[11px] text-[#8a8478]" data-testid="adm-tender-subcategory-none">No subcategories configured under &quot;{form.category}&quot; yet.</div>
            ) : (
              <div className="bg-[#fbf8f1] border border-[#e5e0d8] rounded-sm p-3" data-testid="adm-tender-subcategory-checkboxes">
                <div className="text-[10px] text-[#8a8478] mb-2">Select all that apply.</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {subs.map((s) => {
                    const checked = (form.subcategories || []).includes(s);
                    return (
                      <label key={s} className={`flex items-start gap-2 rounded-sm border px-2.5 py-1.5 text-xs cursor-pointer transition-colors ${checked ? "bg-[#234333]/8 border-[#234333]" : "bg-white border-[#e5e0d8] hover:border-[#234333]/40"}`}>
                        <input
                          type="checkbox"
                          className="mt-0.5 h-3.5 w-3.5 accent-[#234333]"
                          checked={checked}
                          data-testid={`adm-tender-subcategory-${s}`}
                          onChange={(e) => {
                            const cur = form.subcategories || [];
                            const next = e.target.checked ? [...cur, s] : cur.filter((x) => x !== s);
                            setForm({ ...form, subcategories: next });
                          }}
                        />
                        <span className="text-[#1c1a17]">{s}</span>
                      </label>
                    );
                  })}
                </div>
                {(form.subcategories || []).length > 0 && (
                  <div className="mt-2 text-[10px] text-[#6b655b]" data-testid="adm-tender-subcategory-count">{form.subcategories.length} selected</div>
                )}
              </div>
            )}
          </Fld>
          <Fld label="Entity *" full>
            <select
              className={inputCls}
              value={form.entity_scope === "Institute" ? form.institute_id : "__trust__"}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "__trust__") setForm({ ...form, entity_scope: "Trust", institute_id: "" });
                else setForm({ ...form, entity_scope: "Institute", institute_id: v });
              }}
              data-testid="adm-tender-entity"
            >
              <option value="__trust__">GS Mandal Trust</option>
              {masters.institutes.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </Fld>
          <Fld label="Tender Title *" full><input type="text" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={400} data-testid="adm-tender-title" /></Fld>
          <Fld label="Reference Number *"><input type="text" className={inputCls} value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} maxLength={120} data-testid="adm-tender-ref" /></Fld>
          <div />
          <Fld label="Short Description" full><textarea className={`${inputCls} min-h-[70px]`} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} maxLength={800} data-testid="adm-tender-short-desc" /></Fld>
          <Fld label="Long / Detailed Description" full><textarea className={`${inputCls} min-h-[140px]`} value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} maxLength={8000} data-testid="adm-tender-long-desc" /></Fld>
        </div>

        <h3 className="mt-8 trust-eyebrow text-[10px] font-bold text-[#c78e42]">Important Dates</h3>
        <div className="mt-3 grid md:grid-cols-3 gap-3">
          <Fld label="Publication Date"><input type="date" className={inputCls} value={form.publication_date} onChange={(e) => setForm({ ...form, publication_date: e.target.value })} data-testid="adm-tender-pub" /></Fld>
          <Fld label="Bid Opening Date"><input type="date" className={inputCls} value={form.bid_opening_date} onChange={(e) => setForm({ ...form, bid_opening_date: e.target.value })} data-testid="adm-tender-bidopen" /></Fld>
          <Fld label="Last Submission (Date & Time)"><input type="datetime-local" className={inputCls} value={form.last_submission_datetime} onChange={(e) => setForm({ ...form, last_submission_datetime: e.target.value })} data-testid="adm-tender-lastsub" /></Fld>
          <Fld label="Pre-Bid Meeting"><input type="date" className={inputCls} value={form.pre_bid_meeting_date} onChange={(e) => setForm({ ...form, pre_bid_meeting_date: e.target.value })} /></Fld>
          <Fld label="Clarification Deadline"><input type="date" className={inputCls} value={form.clarification_deadline} onChange={(e) => setForm({ ...form, clarification_deadline: e.target.value })} /></Fld>
          <Fld label="Technical Bid Opening"><input type="date" className={inputCls} value={form.technical_bid_opening_date} onChange={(e) => setForm({ ...form, technical_bid_opening_date: e.target.value })} /></Fld>
          <Fld label="Financial Bid Opening"><input type="date" className={inputCls} value={form.financial_bid_opening_date} onChange={(e) => setForm({ ...form, financial_bid_opening_date: e.target.value })} /></Fld>
          <Fld label="Tender Validity"><input type="date" className={inputCls} value={form.validity_date} onChange={(e) => setForm({ ...form, validity_date: e.target.value })} /></Fld>
        </div>

        <h3 className="mt-8 trust-eyebrow text-[10px] font-bold text-[#c78e42]">Eligibility & Submission</h3>
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          <Fld label="Eligibility Criteria"><textarea className={`${inputCls} min-h-[100px]`} value={form.eligibility_criteria} onChange={(e) => setForm({ ...form, eligibility_criteria: e.target.value })} maxLength={4000} /></Fld>
          <Fld label="Submission Details"><textarea className={`${inputCls} min-h-[100px]`} value={form.submission_details} onChange={(e) => setForm({ ...form, submission_details: e.target.value })} maxLength={4000} /></Fld>
        </div>

        <h3 className="mt-8 trust-eyebrow text-[10px] font-bold text-[#c78e42]">Submission Mode</h3>
        <div className="mt-3 bg-[#fbf8f1] border border-[#e5e0d8] rounded-sm p-4" data-testid="adm-tender-mode-section">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className={`flex items-start gap-2 cursor-pointer rounded-sm border p-3 ${form.submission_mode !== "Offline" ? "border-[#234333] bg-white" : "border-[#e5e0d8] hover:border-[#c78e42]"}`}>
              <input
                type="radio"
                name="submission_mode"
                value="Online"
                className="mt-1 h-4 w-4 accent-[#234333]"
                checked={form.submission_mode !== "Offline"}
                onChange={() => setForm({ ...form, submission_mode: "Online" })}
                data-testid="adm-tender-mode-online"
              />
              <span>
                <span className="font-semibold text-[#1c1a17]">Online</span>
                <span className="block text-[11px] text-[#6b655b] mt-0.5">Approved, fee-paid vendors upload Technical + Financial documents via the Vendor Portal.</span>
              </span>
            </label>
            <label className={`flex items-start gap-2 cursor-pointer rounded-sm border p-3 ${form.submission_mode === "Offline" ? "border-[#234333] bg-white" : "border-[#e5e0d8] hover:border-[#c78e42]"}`}>
              <input
                type="radio"
                name="submission_mode"
                value="Offline"
                className="mt-1 h-4 w-4 accent-[#234333]"
                checked={form.submission_mode === "Offline"}
                onChange={() => setForm({ ...form, submission_mode: "Offline" })}
                data-testid="adm-tender-mode-offline"
              />
              <span>
                <span className="font-semibold text-[#1c1a17]">Physical (Offline)</span>
                <span className="block text-[11px] text-[#6b655b] mt-0.5">Vendors must submit a hard copy at the office. The portal shows an information label.</span>
              </span>
            </label>
          </div>
        </div>

        <h3 className="mt-8 trust-eyebrow text-[10px] font-bold text-[#c78e42]">Tender Fee</h3>
        <div className="mt-3 space-y-3 bg-[#fbf8f1] border border-[#e5e0d8] rounded-sm p-4" data-testid="adm-tender-fee-section">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-[#234333]"
              checked={!!form.has_tender_fee}
              onChange={(e) => setForm({ ...form, has_tender_fee: e.target.checked, tender_fee: e.target.checked ? form.tender_fee : 0 })}
              data-testid="adm-tender-fee-applicable"
            />
            <span>
              <span className="font-semibold text-[#1c1a17]">Tender fee is applicable for this tender</span>
              <span className="block text-[11px] text-[#6b655b] mt-0.5">Tick to charge applicants a one-time fee before they can submit their bid.</span>
            </span>
          </label>
          {form.has_tender_fee && (
            <div className="pl-6">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold mb-1.5">Tender Fee (INR) *</div>
              <div className="flex items-center gap-2 max-w-xs">
                <span className="text-sm text-[#6b655b]">₹</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={inputCls}
                  value={form.tender_fee}
                  onChange={(e) => setForm({ ...form, tender_fee: e.target.value })}
                  placeholder="e.g. 5000"
                  data-testid="adm-tender-fee-amount"
                />
              </div>
            </div>
          )}
        </div>

        {current && (
          <div className="mt-8">
            <h3 className="trust-eyebrow text-[10px] font-bold text-[#c78e42]">Attachments (add after saving)</h3>
            <AttachmentManager tender={current} onUpload={uploadAttachment} onDelete={deleteAttachment} />
          </div>
        )}

        <div className="mt-6 border-t border-[#e5e0d8] pt-4 flex flex-wrap items-center justify-end gap-2">
          <button type="button" onClick={() => setPreview(true)} className="bg-white border border-[#234333] text-[#234333] text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-sm inline-flex items-center gap-1" data-testid="adm-tender-preview"><Eye size={12} /> Preview</button>
          <button type="button" onClick={() => saveForm({ publish: false })} disabled={saving} className="bg-[#c78e42] hover:bg-[#a5875a] text-white text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-sm inline-flex items-center gap-1 disabled:opacity-60" data-testid="adm-tender-save-draft">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save as Draft
          </button>
          <button type="button" onClick={() => saveForm({ publish: true })} disabled={saving} className="bg-[#234333] hover:bg-[#1a3125] text-white text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-sm inline-flex items-center gap-1 disabled:opacity-60" data-testid="adm-tender-publish">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Publish
          </button>
        </div>
      </div>

      {preview && <PreviewModal t={{ ...form, ...current }} onClose={() => setPreview(false)} />}
    </div>
  );

  if (view === "detail" && current) return (
    <div className="space-y-5" data-testid="adm-tender-detail">
      <div className="bg-white rounded-sm border border-[#e5e0d8] p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <button type="button" onClick={() => setView("list")} className="text-[10px] uppercase tracking-wider text-[#234333] hover:text-[#c78e42] inline-flex items-center gap-1"><ArrowLeft size={11} /> Tenders</button>
            <h1 className="font-serif text-2xl text-[#1c1a17] mt-1">{current.title}</h1>
            <div className="text-[11px] text-[#6b655b] mt-1">Ref: {current.reference_number || "—"} · <span className="uppercase tracking-wider font-bold text-[#c78e42]">{current.status}</span></div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => setSubmissionsOpen(true)} data-testid="adm-tender-submissions-btn" className="bg-white border border-[#234333] text-[#234333] hover:bg-[#f5f1e8] text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm inline-flex items-center gap-1"><ListChecks size={11} /> Submissions</button>
              <button type="button" onClick={() => openEdit(current)} className="bg-white border border-[#234333] text-[#234333] text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm">Edit</button>
              {current.status === "Draft" && <button type="button" onClick={() => changeStatus(current, "Published")} className="bg-[#234333] hover:bg-[#1a3125] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm inline-flex items-center gap-1"><Send size={11} /> Publish</button>}
              {current.status === "Published" && <>
                <button type="button" onClick={() => setShowExtend(true)} data-testid="adm-tender-extend" className="bg-[#c78e42] hover:bg-[#a5875a] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm inline-flex items-center gap-1"><CalendarClock size={11} /> Extend</button>
                <button type="button" onClick={() => changeStatus(current, "Closed")} className="bg-orange-700 hover:bg-orange-800 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm">Close</button>
                <button type="button" onClick={() => changeStatus(current, "Cancelled")} className="bg-red-700 hover:bg-red-800 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm inline-flex items-center gap-1"><XCircle size={11} /> Cancel</button>
              </>}
              {["Closed", "Cancelled"].includes(current.status) && <button type="button" onClick={() => changeStatus(current, "Archived")} className="bg-slate-700 hover:bg-slate-800 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm inline-flex items-center gap-1"><Archive size={11} /> Archive</button>}
              {user?.role === "super_admin" && <button type="button" onClick={() => setConfirmDelete(current)} className="border border-red-300 text-red-700 hover:bg-red-50 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm inline-flex items-center gap-1"><Trash2 size={11} /> Delete</button>}
            </div>
          )}
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-6">
          <div>
            <RowKV k="Type" v={current.tender_type} />
            <RowKV k="Category" v={current.category} />
            <RowKV k="Subcategory" v={(current.subcategories && current.subcategories.length ? current.subcategories : (current.subcategory ? [current.subcategory] : [])).join(", ")} />
            <RowKV k="Entity" v={current.entity_scope === "Institute" ? current.institute_name : "GS Mandal Trust"} />
            <RowKV k="Publication" v={fmtDate(current.publication_date)} />
            <RowKV k="Bid Opening" v={fmtDate(current.bid_opening_date)} />
            <RowKV k="Last Submission" v={current.last_submission_datetime} />
            <RowKV k="Pre-Bid Meeting" v={fmtDate(current.pre_bid_meeting_date)} />
          </div>
          <div>
            <RowKV k="Clarification Deadline" v={fmtDate(current.clarification_deadline)} />
            <RowKV k="Technical Bid" v={fmtDate(current.technical_bid_opening_date)} />
            <RowKV k="Financial Bid" v={fmtDate(current.financial_bid_opening_date)} />
            <RowKV k="Validity" v={fmtDate(current.validity_date)} />
            <RowKV k="Created" v={fmtDate(current.created_at)} />
            <RowKV k="Published" v={fmtDate(current.published_at)} />
            <RowKV k="Tender Fee" v={current.has_tender_fee ? `₹ ${Number(current.tender_fee || 0).toLocaleString("en-IN")}` : "Not applicable"} />
            <RowKV k="Submission Mode" v={current.submission_mode === "Offline" ? "Physical (Offline)" : "Online"} />
          </div>
        </div>

        {current.short_description && <Sub title="Short Description"><div className="text-xs whitespace-pre-wrap">{current.short_description}</div></Sub>}
        {current.long_description && <Sub title="Detailed Description"><div className="text-xs whitespace-pre-wrap">{current.long_description}</div></Sub>}
        {current.eligibility_criteria && <Sub title="Eligibility"><div className="text-xs whitespace-pre-wrap">{current.eligibility_criteria}</div></Sub>}
        {current.submission_details && <Sub title="Submission Details"><div className="text-xs whitespace-pre-wrap">{current.submission_details}</div></Sub>}

        <Sub title="Attachments"><AttachmentManager tender={current} onUpload={uploadAttachment} onDelete={deleteAttachment} /></Sub>

        <Sub title="Corrigenda">
          <div className="space-y-2">
            {(current.corrigenda || []).length === 0 && <div className="text-xs text-[#6b655b]">No corrigenda yet.</div>}
            {(current.corrigenda || []).map((c) => (
              <div key={c.id} className="border-l-2 border-[#c78e42] bg-[#f5f1e8] pl-3 py-2 rounded-r-sm">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs font-semibold text-[#1c1a17]">{c.title}</div>
                  <div className="text-[10px] text-[#6b655b]">{fmtDate(c.corr_date)}</div>
                </div>
                {c.note && <div className="text-[11px] text-[#3E3E3E] mt-1 whitespace-pre-wrap">{c.note}</div>}
                {c.attachment_url && <a href={c.attachment_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-[#234333] hover:text-[#c78e42]"><Download size={10} /> PDF</a>}
              </div>
            ))}
          </div>
          {canEdit && (
            <div className="mt-3 bg-[#fbf8f1] border border-[#e5e0d8] rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#c78e42] font-bold">Add Corrigendum</div>
              <div className="mt-2 grid md:grid-cols-3 gap-2">
                <input type="text" placeholder="Title" className={inputCls} value={corr.title} onChange={(e) => setCorr({ ...corr, title: e.target.value })} data-testid="adm-tender-corr-title" />
                <input type="date" className={inputCls} value={corr.corr_date} onChange={(e) => setCorr({ ...corr, corr_date: e.target.value })} />
                <label className="text-xs bg-white border border-[#e5e0d8] rounded-sm px-3 py-2 cursor-pointer inline-flex items-center gap-2"><Upload size={12} /> {corr.file ? corr.file.name : "Attach PDF (optional)"}<input type="file" accept="application/pdf" hidden onChange={(e) => setCorr({ ...corr, file: e.target.files?.[0] || null })} /></label>
              </div>
              <textarea className={`${inputCls} mt-2 min-h-[70px]`} placeholder="Corrigendum notes / changes" value={corr.note} onChange={(e) => setCorr({ ...corr, note: e.target.value })} />
              <button type="button" onClick={addCorrigendum} className="mt-2 bg-[#234333] hover:bg-[#1a3125] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm inline-flex items-center gap-1" data-testid="adm-tender-corr-add"><Plus size={11} /> Add Corrigendum</button>
            </div>
          )}
        </Sub>
      </div>

      {showExtend && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowExtend(false)}>
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#234333] text-white px-5 py-3 flex items-center justify-between">
              <div className="font-serif text-lg">Extend Deadline</div>
              <button type="button" onClick={() => setShowExtend(false)}><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-[#6b655b]">Current: {current.last_submission_datetime || "—"}</div>
              <input type="datetime-local" className={inputCls} value={extendDt} onChange={(e) => setExtendDt(e.target.value)} data-testid="adm-tender-extend-input" />
              <div className="text-[10px] text-[#6b655b]">A corrigendum entry will be added automatically.</div>
              <button type="button" onClick={extendDeadline} className="w-full bg-[#c78e42] hover:bg-[#a5875a] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm" data-testid="adm-tender-extend-confirm">Extend</button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-xl">Delete tender?</AlertDialogTitle>
            <AlertDialogDescription>All attachments and corrigenda PDFs will be permanently deleted. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-700 hover:bg-red-800 text-white" data-testid="adm-tender-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {submissionsOpen && <SubmissionsDrawer tender={current} onClose={() => setSubmissionsOpen(false)} />}
    </div>
  );
  return (
    <div className="space-y-5" data-testid="adm-tenders-list">
      <div className="bg-white rounded-sm border border-[#e5e0d8] p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
            <h1 className="font-serif text-2xl text-[#1c1a17] flex items-center gap-2"><FileText size={18} /> Tender Management</h1>
            <div className="text-sm text-[#6b655b] mt-0.5">Create, publish and manage tenders across GS Mandal Trust and its institutes.</div>
          </div>
          {canEdit && <button type="button" onClick={openCreate} data-testid="adm-tender-create" className="bg-[#234333] hover:bg-[#1a3125] text-white text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-sm inline-flex items-center gap-1"><Plus size={12} /> Create New Tender</button>}
        </div>

        <div className="mt-5 flex flex-wrap gap-2 border-b border-[#e5e0d8]">
          {STATUSES.map((s) => (
            <button key={s} type="button" onClick={() => setTab(s)} data-testid={`adm-tender-tab-${s.toLowerCase()}`} className={`text-[11px] uppercase tracking-wider font-bold px-4 py-2.5 transition-colors ${tab === s ? "bg-[#234333] text-white" : "text-[#6b655b] hover:bg-[#f5f1e8]"}`}>
              {s}{tab === s && ` (${list.length})`}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="text-sm text-[#6b655b] flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
          ) : list.length === 0 ? (
            <div className="bg-[#f5f1e8] rounded-sm p-8 text-center text-sm text-[#6b655b]">No {tab.toLowerCase()} tenders.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#6b655b] border-b border-[#e5e0d8]">
                    <th className="py-2 pr-3">Updated</th>
                    <th className="py-2 pr-3">Title / Ref</th>
                    <th className="py-2 pr-3">Type / Category</th>
                    <th className="py-2 pr-3">Entity</th>
                    <th className="py-2 pr-3">Last Submission</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((t) => (
                    <tr key={t.id} className="border-b border-[#f0ebe0] hover:bg-[#fbf8f1]" data-testid={`adm-tender-row-${t.id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(t.updated_at)}</td>
                      <td className="py-2 pr-3">
                        <div className="font-semibold text-[#1c1a17]">{t.title}</div>
                        <div className="text-[10px] text-[#6b655b] font-mono">{t.reference_number || "—"}</div>
                      </td>
                      <td className="py-2 pr-3">
                        <div>{t.tender_type}</div>
                        <div className="text-[10px] text-[#6b655b]">{[t.category, ...(t.subcategories && t.subcategories.length ? t.subcategories : (t.subcategory ? [t.subcategory] : []))].filter(Boolean).join(" · ") || "—"}</div>
                      </td>
                      <td className="py-2 pr-3">{t.entity_scope === "Institute" ? t.institute_name : "GS Mandal"}</td>
                      <td className="py-2 pr-3">{t.last_submission_datetime || "—"}</td>
                      <td className="py-2 pr-3">
                        <button type="button" onClick={() => openDetail(t)} className="inline-flex items-center gap-1 bg-white border border-[#e5e0d8] hover:border-[#234333] text-[#234333] px-2 py-1 rounded-sm text-[10px] font-bold uppercase" data-testid={`adm-tender-open-${t.id}`}><Eye size={11} /> Open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Fld({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}
function Sub({ title, children }) {
  return <div className="mt-6"><div className="text-[10px] uppercase tracking-[0.25em] text-[#c78e42] font-bold">{title}</div><div className="mt-2">{children}</div></div>;
}
function RowKV({ k, v }) {
  return <div className="flex gap-2 text-xs py-0.5"><div className="text-[#6b655b] w-40 shrink-0">{k}</div><div className="text-[#1c1a17] font-medium break-words">{v || "—"}</div></div>;
}

function AttachmentManager({ tender, onUpload, onDelete }) {
  const rfe = (tender.attachments || []).find((a) => a.kind === "rfe");
  const supporting = (tender.attachments || []).filter((a) => a.kind !== "rfe");
  const [busy, setBusy] = useState(false);
  const pick = async (e, kind) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    await onUpload(f, kind, f.name);
    setBusy(false);
  };
  return (
    <div className="space-y-3">
      <div className="border border-[#e5e0d8] rounded-sm p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#c78e42] font-bold">RFE / Tender Document (required)</div>
            <div className="text-[10px] text-[#6b655b]">PDF · max 10 MB · alphanumeric filename only</div>
          </div>
          {!rfe && (
            <label className="text-xs bg-[#234333] hover:bg-[#1a3125] text-white uppercase tracking-wider font-bold px-3 py-1.5 rounded-sm cursor-pointer inline-flex items-center gap-1">
              <Upload size={11} /> {busy ? "Uploading…" : "Upload RFE"}
              <input type="file" accept="application/pdf" hidden onChange={(e) => pick(e, "rfe")} data-testid="adm-tender-upload-rfe" />
            </label>
          )}
        </div>
        {rfe && (
          <div className="mt-2 bg-[#f5f1e8] rounded-sm px-3 py-2 flex items-center justify-between gap-3">
            <a href={rfe.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#234333] font-semibold hover:text-[#c78e42] inline-flex items-center gap-1"><FileText size={12} /> {rfe.label}</a>
            <button type="button" onClick={() => onDelete(rfe)} className="text-red-700 hover:bg-red-50 rounded p-1"><Trash2 size={12} /></button>
          </div>
        )}
      </div>
      <div className="border border-[#e5e0d8] rounded-sm p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#c78e42] font-bold">Supporting Documents (up to 5)</div>
            <div className="text-[10px] text-[#6b655b]">PDF · max 10 MB each · alphanumeric filenames only</div>
          </div>
          {supporting.length < 5 && (
            <label className="text-xs bg-white border border-[#234333] text-[#234333] uppercase tracking-wider font-bold px-3 py-1.5 rounded-sm cursor-pointer inline-flex items-center gap-1">
              <Upload size={11} /> Add supporting
              <input type="file" accept="application/pdf" hidden onChange={(e) => pick(e, "supporting")} data-testid="adm-tender-upload-sup" />
            </label>
          )}
        </div>
        {supporting.length > 0 && (
          <div className="mt-2 space-y-1">
            {supporting.map((a) => (
              <div key={a.public_id} className="bg-white border border-[#e5e0d8] rounded-sm px-3 py-2 flex items-center justify-between gap-3">
                <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1c1a17] hover:text-[#c78e42] inline-flex items-center gap-1"><FileText size={12} /> {a.label}</a>
                <button type="button" onClick={() => onDelete(a)} className="text-red-700 hover:bg-red-50 rounded p-1"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewModal({ t, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="adm-tender-preview-modal">
      <div className="bg-white rounded-sm shadow-2xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#0D5C63] text-white px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#F26419]">Public Preview · {t.tender_type || "Tender"}</div>
            <div className="font-serif text-lg">{t.title || "Untitled tender"}</div>
          </div>
          <button type="button" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="p-5 text-sm">
          <div className="text-xs text-[#5B5B5B]">Ref: {t.reference_number || "—"} · Entity: {t.entity_scope === "Institute" ? (t.institute_name || "Institute") : "GS Mandal Trust"}</div>
          {t.short_description && <p className="mt-3 text-[#3E3E3E]">{t.short_description}</p>}
          {t.last_submission_datetime && <div className="mt-3 bg-[#F5EFE3] rounded-md px-3 py-2 text-xs font-semibold text-[#0D5C63]">Last submission: {t.last_submission_datetime}</div>}
          {t.long_description && <div className="mt-4 text-xs whitespace-pre-wrap text-[#1A1A1A]">{t.long_description}</div>}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// SubmissionsDrawer — shows every vendor submission for a given
// tender. Trust admins download Technical + Financial PDFs from
// here (Cloudinary raw URLs open in a new tab).
// -------------------------------------------------------------
function SubmissionsDrawer({ tender, onClose }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  const load = React.useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/admin/tenders/${tender.id}/submissions`);
      setRows(data?.submissions || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load submissions");
    } finally { setLoading(false); }
  }, [tender.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex justify-end"
      onClick={onClose}
      data-testid="adm-tender-submissions-drawer"
    >
      <div
        className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0F0F10] text-white px-6 py-4 flex items-start justify-between gap-4 border-b-4 border-[#c78e42]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#c78e42] font-semibold">Vendor Submissions</div>
            <h2 className="font-serif text-xl leading-tight mt-1">{tender.title}</h2>
            <div className="text-[11px] text-white/70 mt-1">
              Ref: {tender.reference_number || "—"} · Mode:{" "}
              <span className="font-semibold text-[#c78e42]">{tender.submission_mode === "Offline" ? "Physical (Offline)" : "Online"}</span>
              {tender.last_submission_datetime && <> · Closes: {tender.last_submission_datetime}</>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/70 hover:text-white p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6">
          {(tender.submission_mode === "Offline") && (
            <div className="mb-4 bg-[#F5EFE3] border border-[#c78e42]/40 rounded-sm px-4 py-3 text-sm text-[#8a5a1a]">
              This tender is set to <b>Physical (Offline)</b> submission. Vendors deposit hard-copy bids at the office; the portal-upload flow is disabled for this tender.
            </div>
          )}
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[#6b655b]"><Loader2 size={14} className="animate-spin" /> Loading submissions…</div>
          ) : err ? (
            <div className="text-sm text-red-700">{err}</div>
          ) : rows.length === 0 ? (
            <div className="bg-[#F5F1E8] rounded-sm p-8 text-center text-sm text-[#6b655b]" data-testid="adm-tender-submissions-empty">
              No vendors have uploaded documents yet.
            </div>
          ) : (
            <div className="space-y-4">
              {rows.map((s) => (
                <div
                  key={s.id || s.vendor_registration_id}
                  data-testid={`adm-tender-submission-${s.vendor_registration_id}`}
                  className="border border-[#e5e0d8] rounded-sm p-4 bg-[#fbf8f1]"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 text-[#0F0F10]">
                        <User size={14} strokeWidth={1.6} />
                        <span className="font-serif text-lg font-semibold">{s.vendor_firm || "—"}</span>
                        {s.vendor_status && (
                          <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm ${
                            s.vendor_status === "Approved"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-neutral-100 text-neutral-700"
                          }`}>
                            {s.vendor_status}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[11px] text-[#6b655b] flex flex-wrap items-center gap-3">
                        {s.vendor_email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {s.vendor_email}</span>}
                        {s.vendor_mobile && <span className="inline-flex items-center gap-1"><Phone size={11} /> {s.vendor_mobile}</span>}
                      </div>
                    </div>
                    <div className="text-[10px] text-[#6b655b]">
                      Updated: {s.updated_at ? new Date(s.updated_at).toLocaleString() : "—"}
                    </div>
                  </div>

                  <div className="mt-4 grid md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#c78e42] font-bold mb-2">
                        Technical Documents ({(s.technical_docs || []).length})
                      </div>
                      {(s.technical_docs || []).length === 0 ? (
                        <div className="text-xs text-[#8a8478] italic">No files uploaded.</div>
                      ) : (
                        <ul className="space-y-1.5">
                          {(s.technical_docs || []).map((d, i) => (
                            <li key={d.id || i} className="flex items-center justify-between gap-2 bg-white border border-[#e5e0d8] rounded-sm px-3 py-2 text-xs">
                              <span className="truncate flex-1 text-[#1c1a17]">{d.filename || `Doc ${i + 1}`}</span>
                              <span className="text-[#8a8478] shrink-0">{d.size ? `${Math.round(d.size / 1024)} KB` : ""}</span>
                              <a href={d.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#234333] hover:text-[#c78e42] font-semibold" data-testid={`adm-tender-tech-open-${d.id}`}>
                                Open <ExternalLink size={11} />
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#c78e42] font-bold mb-2">
                        Financial Document
                      </div>
                      {!s.financial_doc ? (
                        <div className="text-xs text-[#8a8478] italic">Not uploaded.</div>
                      ) : (
                        <div className="flex items-center justify-between gap-2 bg-white border border-[#e5e0d8] rounded-sm px-3 py-2 text-xs">
                          <span className="truncate flex-1 text-[#1c1a17]">{s.financial_doc.filename || "Financial.pdf"}</span>
                          <span className="text-[#8a8478] shrink-0">{s.financial_doc.size ? `${Math.round(s.financial_doc.size / 1024)} KB` : ""}</span>
                          <a href={s.financial_doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#234333] hover:text-[#c78e42] font-semibold" data-testid={`adm-tender-fin-open-${s.vendor_registration_id}`}>
                            Open <ExternalLink size={11} />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
